import { useEffect, useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter } from '@/components/ui/drawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CountryCombobox from '@/components/passport/CountryCombobox';
import { MONTH_NAMES, type PassportEntryRow } from '@/lib/passport';
import { usePassportEligibleCountries } from '@/hooks/usePassport';

interface AddPassportEntrySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingEntry?: PassportEntryRow | null;
  onSubmit: (input: { countryId: string; destinationName: string; month: number }) => void;
  isSubmitting?: boolean;
}

const AddPassportEntrySheet = ({ open, onOpenChange, editingEntry, onSubmit, isSubmitting }: AddPassportEntrySheetProps) => {
  const isMobile = useIsMobile();
  const { data: countries = [], isLoading: countriesLoading } = usePassportEligibleCountries();

  const [countryId, setCountryId] = useState<string>('');
  const [destinationName, setDestinationName] = useState('');
  const [month, setMonth] = useState<string>('');

  const isEditing = !!editingEntry;

  useEffect(() => {
    if (!open) return;
    if (editingEntry) {
      setCountryId(editingEntry.country_id);
      setDestinationName(editingEntry.destination_name);
      setMonth(String(editingEntry.month));
    } else {
      setCountryId('');
      setDestinationName('');
      setMonth('');
    }
  }, [open, editingEntry]);

  const canSubmit = !!countryId && destinationName.trim().length > 0 && !!month;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({ countryId, destinationName: destinationName.trim(), month: Number(month) });
  };

  const title = isEditing ? 'Edit Passport entry' : 'Add to Passport';
  const description = 'Country, destination, and time of year — that\'s it.';
  const submitLabel = isEditing ? 'Save changes' : 'Add to Passport';

  const formBody = (
    <div className="space-y-6 px-1">
      <div className="space-y-2">
        <Label htmlFor="passport-country">Country</Label>
        <CountryCombobox
          id="passport-country"
          countries={countries}
          value={countryId}
          onChange={setCountryId}
          loading={countriesLoading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="passport-destination">Destination</Label>
        <Input
          id="passport-destination"
          placeholder="e.g. Red Island"
          value={destinationName}
          onChange={(e) => setDestinationName(e.target.value)}
          maxLength={120}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="passport-month">Month</Label>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger id="passport-month">
            <SelectValue placeholder="Select a month" />
          </SelectTrigger>
          <SelectContent>
            {MONTH_NAMES.map((name, idx) => (
              <SelectItem key={name} value={String(idx + 1)}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-serif">{title}</DrawerTitle>
            <DrawerDescription>{description}</DrawerDescription>
          </DrawerHeader>
          <div className="pb-2">{formBody}</div>
          <DrawerFooter>
            <Button onClick={handleSubmit} disabled={!canSubmit || isSubmitting}>
              {submitLabel}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {formBody}
        <DialogFooter>
          <Button onClick={handleSubmit} disabled={!canSubmit || isSubmitting}>
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddPassportEntrySheet;
